import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useAction } from 'wasp/client/operations';
import { getFieldConfigById, updateFieldConfig } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { isAdministrator } from '../../client/lib/platformRole';
import { Settings, Save, X, Plus, Trash2 } from 'lucide-react';
import LoadingSpinner from '../layout/LoadingSpinner';

interface FieldOption {
  value: string;
  label: string;
  icon?: string;
}

interface FieldConfigFormData {
  fieldType: string;
  label: string;
  description: string;
  options: FieldOption[];
  defaultValue: string;
  isRequired: boolean;
  displayOrder: number;
  isActive: boolean;
}

interface FormErrors {
  fieldType?: string;
  label?: string;
  submit?: string;
}

const FIELD_TYPES = [
  { value: 'DROPDOWN', label: 'Dropdown', hasOptions: true },
  { value: 'RADIO', label: 'Radio Buttons', hasOptions: true },
  { value: 'TOGGLE', label: 'Toggle Switch', hasOptions: false },
  { value: 'TEXT', label: 'Text Input', hasOptions: false },
  { value: 'TEXTAREA', label: 'Text Area', hasOptions: false },
  { value: 'NUMBER', label: 'Number Input', hasOptions: false },
  { value: 'DATE', label: 'Date Picker', hasOptions: false },
];

export default function EditFieldConfigPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  const { data: fieldConfig, isLoading: isLoadingConfig, error: loadError } = useQuery(
    getFieldConfigById,
    { id: id || '' },
    { enabled: !!id }
  );

  const updateFieldConfigFn = useAction(updateFieldConfig);

  const [formData, setFormData] = useState<FieldConfigFormData>({
    fieldType: '',
    label: '',
    description: '',
    options: [],
    defaultValue: '',
    isRequired: false,
    displayOrder: 0,
    isActive: true,
  });

  // Populate form when data loads
  useEffect(() => {
    if (fieldConfig) {
      setFormData({
        fieldType: fieldConfig.fieldType || '',
        label: fieldConfig.label || '',
        description: fieldConfig.description || '',
        options: Array.isArray(fieldConfig.options) ? (fieldConfig.options as unknown as FieldOption[]) : [],
        defaultValue: fieldConfig.defaultValue || '',
        isRequired: fieldConfig.isRequired || false,
        displayOrder: fieldConfig.displayOrder || 0,
        isActive: fieldConfig.isActive !== undefined ? fieldConfig.isActive : true,
      });
    }
  }, [fieldConfig]);

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600">You must be logged in to edit field configurations.</p>
        </div>
      </div>
    );
  }

  if (!isAdministrator(user)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600">You must be an admin to edit field configurations.</p>
        </div>
      </div>
    );
  }

  if (isLoadingConfig) {
    return <LoadingSpinner />;
  }

  if (loadError || !fieldConfig) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Field Configuration Not Found</h2>
          <p className="text-gray-600 mb-6">
            {loadError?.message || 'The field configuration could not be found.'}
          </p>
          <button
            onClick={() => navigate('/admin/field-configs')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Field Configurations
          </button>
        </div>
      </div>
    );
  }

  const handleChange = (field: keyof FieldConfigFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const selectedFieldType = FIELD_TYPES.find((ft) => ft.value === formData.fieldType);
  const needsOptions = selectedFieldType?.hasOptions;

  // Add new option
  const addOption = () => {
    setFormData((prev) => ({
      ...prev,
      options: [...prev.options, { value: '', label: '', icon: '' }],
    }));
  };

  // Update option
  const updateOption = (index: number, field: keyof FieldOption, value: string) => {
    const newOptions = [...formData.options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setFormData((prev) => ({ ...prev, options: newOptions }));
  };

  // Remove option
  const removeOption = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.fieldType) {
      newErrors.fieldType = 'Field type is required';
    }

    if (!formData.label.trim()) {
      newErrors.label = 'Label is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      // Filter out empty options and clean up data
      const cleanOptions = needsOptions
        ? formData.options
            .filter((opt) => opt.value && opt.label)
            .map((opt) => ({
              value: opt.value,
              label: opt.label,
              ...(opt.icon && { icon: opt.icon }),
            }))
        : undefined;

      await updateFieldConfigFn({
        id: id || '',
        fieldType: formData.fieldType,
        label: formData.label,
        description: formData.description || undefined,
        options: cleanOptions,
        defaultValue: formData.defaultValue || undefined,
        isRequired: formData.isRequired,
        displayOrder: formData.displayOrder,
        isActive: formData.isActive,
      });

      navigate('/admin/field-configs');
    } catch (error: any) {
      console.error('Error updating field config:', error);
      setErrors({
        submit: error.message || 'Failed to update field configuration. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/field-configs');
  };

  return (
    <div className="flex h-full">
        <div className="flex-1 bg-card">
          <div className="flex h-full">
            {/* Main Content */}
            <div className="flex-1 p-6 py-8">
              {/* Header */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <Settings className="w-8 h-8 text-blue-600" />
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">Edit Field Configuration</h1>
                    <p className="text-gray-600">
                      {fieldConfig.contentType} / {fieldConfig.fieldKey}
                    </p>
                  </div>
                </div>
              </div>

              {/* Error Alert */}
              {errors.submit && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800">{errors.submit}</p>
                </div>
              )}

              {/* Form */}
              <form id="field-config-form" onSubmit={handleSubmit}>
                <div className="space-y-6">
                  {/* Read-only fields */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-900 mb-3">
                      Read-only Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Content Type</p>
                        <p className="font-medium text-gray-900">{fieldConfig.contentType}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Field Key</p>
                        <p className="font-mono font-medium text-gray-900">
                          {fieldConfig.fieldKey}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Label */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Label <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.label}
                      onChange={(e) => handleChange('label', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                        errors.label ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Help Category"
                      required
                    />
                    {errors.label && <p className="text-red-600 text-sm mt-1">{errors.label}</p>}
                    <p className="text-gray-500 text-sm mt-1">Display label shown to users</p>
                  </div>

                  {/* Field Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Field Type <span className="text-red-600">*</span>
                    </label>
                    <select
                      value={formData.fieldType}
                      onChange={(e) => handleChange('fieldType', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                        errors.fieldType ? 'border-red-500' : 'border-gray-300'
                      }`}
                      required
                    >
                      <option value="">Select field type...</option>
                      {FIELD_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    {errors.fieldType && (
                      <p className="text-red-600 text-sm mt-1">{errors.fieldType}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Description (Optional)
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="Help text to guide users"
                    />
                  </div>

                  {/* Options (for DROPDOWN and RADIO) */}
                  {needsOptions && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-sm font-medium text-gray-900">Options</label>
                        <button
                          type="button"
                          onClick={addOption}
                          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          Add Option
                        </button>
                      </div>

                      <div className="space-y-3">
                        {formData.options.map((option, index) => (
                          <div key={index} className="flex gap-3 items-start">
                            <input
                              type="text"
                              value={option.value}
                              onChange={(e) => updateOption(index, 'value', e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                              placeholder="value (e.g., rides_transport)"
                            />
                            <input
                              type="text"
                              value={option.label}
                              onChange={(e) => updateOption(index, 'label', e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                              placeholder="Label (e.g., Rides & transport)"
                            />
                            <input
                              type="text"
                              value={option.icon || ''}
                              onChange={(e) => updateOption(index, 'icon', e.target.value)}
                              className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                              placeholder="Icon (optional)"
                            />
                            <button
                              type="button"
                              onClick={() => removeOption(index)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>

                      {formData.options.length === 0 && (
                        <p className="text-gray-500 text-sm mt-2">No options added yet</p>
                      )}
                    </div>
                  )}

                  {/* Settings Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        Display Order
                      </label>
                      <input
                        type="number"
                        value={formData.displayOrder}
                        onChange={(e) => handleChange('displayOrder', parseInt(e.target.value))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        min="0"
                      />
                      <p className="text-gray-500 text-sm mt-1">Order in which field appears</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        Default Value
                      </label>
                      <input
                        type="text"
                        value={formData.defaultValue}
                        onChange={(e) => handleChange('defaultValue', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        placeholder="Optional default value"
                      />
                    </div>
                  </div>

                  {/* Checkboxes */}
                  <div className="space-y-3">
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={formData.isRequired}
                        onChange={(e) => handleChange('isRequired', e.target.checked)}
                        className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-gray-900">Required field</span>
                    </label>

                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => handleChange('isActive', e.target.checked)}
                        className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-gray-900">Active (visible to users)</span>
                    </label>
                  </div>
                </div>
              </form>
            </div>

            {/* Right Sidebar */}
            <div className="w-80 border-l border-border flex flex-col sticky top-0 self-start bg-card min-h-screen">
              {/* Actions */}
              <div className="p-6 pt-8 border-b border-border">
                <div className="flex flex-col gap-3">
                  <button
                    type="submit"
                    form="field-config-form"
                    disabled={isLoading}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Updating...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Update Field Config
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={isLoading}
                    className="w-full px-4 py-3 text-foreground bg-card border border-input rounded-lg hover:bg-muted transition-colors font-medium flex items-center justify-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                </div>
              </div>

              {/* Help Section */}
              <div className="p-6 border-b border-border">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Field Configuration Tips</h3>
                <div className="space-y-3 text-sm text-gray-600">
                  <p>
                    <strong>Content Type & Field Key:</strong> These cannot be changed after
                    creation to maintain data integrity.
                  </p>
                  <p>
                    <strong>Field Type:</strong> Changing the field type may affect existing data.
                    Use with caution.
                  </p>
                  <p>
                    <strong>Options:</strong> For dropdown and radio fields, ensure all options are
                    properly configured before activating.
                  </p>
                  <p>
                    <strong>Active Status:</strong> Inactive fields are hidden from users but data
                    is preserved.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
}



